import {
  ComponentsContainer,
  ConfigurableFormItem,
  executeScript,
  getStyle,
  IToolboxComponent,
  useAvailableConstantsData,
  validateConfigurableComponentSettings,
} from "@shesha-io/reactjs";
import { IMapBoundaryProps } from "./model";
import { GlobalOutlined } from "@ant-design/icons";
import React, { useRef } from "react";
import Map from "../../components/mapping";
import { settingsForm } from "./settings";
import { LngLatLike } from "mapbox-gl";
import { nanoid } from "nanoid";
import { MapProvider } from "../../components/mapping/provider";
import { Modal, Input, Checkbox, Select } from "antd";
import { useHttpClient } from "@shesha-io/reactjs";
import { centroid } from "@turf/centroid";

// Replace previous getRandomColor with palette-based version
const colorPalette = [
  "#FF6633", "#FFB399", "#FF33FF", "#FFFF99", "#00B3E6",
  "#E6B333", "#3366E6", "#999966", "#99FF99", "#B34D4D",
  "#80B300", "#809900", "#E6B3B3", "#6680B3", "#66991A",
  "#FF99E6", "#CCFF1A", "#FF1A66", "#E6331A", "#33FFCC"
];
function getRandomColor() {
  return colorPalette[Math.floor(Math.random() * colorPalette.length)];
}

// Enrich polygon with centroid
function enrichPolygonWithCenterAndArea(feature: any): void {
  if (feature.geometry?.type === "Polygon") {
    try {
      const c = centroid(feature as any);
      if (c && c.geometry && Array.isArray(c.geometry.coordinates)) {
        const [lng, lat] = c.geometry.coordinates;
        feature.properties.center = [lng, lat];
      }
    } catch (err) {
      // ignore centroid errors
    }
  }
}

const MapToolBox: IToolboxComponent<IMapBoundaryProps> = {
  type: "mapping",
  isInput: false,
  name: "Map",
  icon: <GlobalOutlined />,
  Factory: ({ model }) => {
    const allScriptArgs = useAvailableConstantsData();
    const drawRef = useRef<MapboxDraw | null>(null);
    const hiddenBtnRef = useRef<HTMLButtonElement | null>(null);
    const hiddenDeleteBtnRef = useRef<HTMLButtonElement | null>(null);
    const [mapKey, setMapKey] = React.useState(() => nanoid());
    const [modalOpen, setModalOpen] = React.useState(false);
    const [polygonName, setPolygonName] = React.useState("");
    const [pendingPolygon, setPendingPolygon] = React.useState<any | null>(null);
    const [isRegisteredForVat, setIsRegisteredForVat] = React.useState(false);
    const [color, setColor] = React.useState('#4fb948');
    const [irrigationType, setIrrigationType] = React.useState<number | undefined>(undefined);
    // Use API to get irrigation type options
    const [irrigationTypeOptions, setIrrigationTypeOptions] = React.useState<{ value: number, label: string }[]>([]);
    const httpClient = useHttpClient();
    const [owner, setOwner] = React.useState("");
    const [ownerOptions, setOwnerOptions] = React.useState([]);
    const [showOwner, setShowOwner] = React.useState(false);
    const [showIrrigationType, setShowIrrigationType] = React.useState(false);

    // Expose hard reset action for configurable actions
    React.useEffect(() => {
      (window as any).hardResetMap = () => {
        setMapKey(nanoid());
      };
      return () => {
        delete (window as any).hardResetMap;
      };
    }, []);

    const getPanelStyle = {
      ...getStyle(model.panelStyle),
    };

    const handleBoundaryDraw = async (polygons: any) => {
      return model.onSave
        ? await executeScript(model.onSave, {
            ...allScriptArgs,
            polygons,
          })
        : {};
    };

    const onGetBoundaries = async (): Promise<any> => {
      return model.onGetBoundaries
        ? await executeScript(model.onGetBoundaries, allScriptArgs)
        : [];
    };

    const onGetCenter = async (): Promise<LngLatLike> => {
      return model.onGetCenter
        ? await executeScript(model.onGetCenter, allScriptArgs)
        : [28.191795, -25.854708];
    };

    // Called after drawing a polygon (from Map)
    const handlePolygonDrawn = (polygon: any) => {
      setPendingPolygon(polygon);
      setPolygonName("");
      setIsRegisteredForVat(false); // Reset VAT checkbox
      setOwner(""); // Reset owner
      setColor(getRandomColor()); // Assign random color
      setIrrigationType(undefined); // Reset irrigation type
      // Determine which fields to show based on path
      if (typeof window !== 'undefined') {
        const path = window.location.pathname.toLowerCase();
        if (path.includes('landholding')) {
          setShowOwner(true);
        } else if (path.includes('farm')) {
          setShowIrrigationType(true);
          setIsRegisteredForVat(false);
        }
      }
      setModalOpen(true);
    };

    const handleModalSave = async () => {
      if (pendingPolygon) {
        const polygonWithName = {
          ...pendingPolygon,
          properties: { ...pendingPolygon.properties, name: polygonName, color },
        };
        // Enrich polygon with area and centroid before saving
        enrichPolygonWithCenterAndArea(polygonWithName);
        
        const irrigationTypeValue = typeof irrigationType === 'number' ? irrigationType : null;
        try {
          const result = await handleBoundaryDraw([{ ...polygonWithName, _ui: { isRegisteredForVat, owner, irrigationType: irrigationTypeValue } }]);
          if (result !== false) {
            setMapKey(nanoid());
            setModalOpen(false);
            setPendingPolygon(null);
            setPolygonName("");
            setIsRegisteredForVat(false);
            setOwner("");
            setColor(getRandomColor());
            setIrrigationType(undefined);
          }
        } catch (error) {
          console.error("Failed to save polygon:", error);
          // Consider showing user feedback via message.error() or similar
        }
      } else {
        setModalOpen(false);
      }
    };

    const handleModalCancel = () => {
      setModalOpen(false);
      setPolygonName("");
      setPendingPolygon(null);
    };

    React.useEffect(() => {
      if (modalOpen) {
        httpClient.get("/api/dynamic/Shesha/Person/Crud/GetAll?properties=fullName")
          .then(data => {
            const result = data?.data?.result || data;
            if (Array.isArray(result?.items)) {
              setOwnerOptions(result.items.map((item: any) => ({ value: item.fullName, label: item.fullName })));
            } else {
              setOwnerOptions([]);
            }
          })
          .catch(() => setOwnerOptions([]));
        // Fetch irrigation type reference list from API
        httpClient.get("/api/services/app/ReferenceList/GetByName?module=boxfusion.agrierp&name=Boxfusion.Agrierp.IrrigationType")
          .then(data => {
            const result = data?.data?.result || data;
            if (Array.isArray(result?.items)) {
              setIrrigationTypeOptions(result.items.map((item: any) => ({ value: item.itemValue, label: item.item })));
            } else {
              setIrrigationTypeOptions([]);
            }
          })
          .catch(() => setIrrigationTypeOptions([]));
      }
    }, [modalOpen]);

    return (
      <ConfigurableFormItem model={model}>
        {(_value) => (
          <div>
            <MapProvider
              drawRef={drawRef}
              saveBtnRef={hiddenBtnRef}
              deleteBtnRef={hiddenDeleteBtnRef}
            >
              <Map
                key={mapKey}
                accessToken={model.accessToken}
                zoomLevel={model.zoomLevel}
                onSave={handleBoundaryDraw}
                onPolygonDrawn={handlePolygonDrawn}
                onGetBoundaries={onGetBoundaries}
                onGetCenter={onGetCenter}
                drawRef={drawRef}
                saveBtnRef={hiddenBtnRef}
                deleteBtnRef={hiddenDeleteBtnRef}
                enableCustomPanel={model.enableCustomPanel}
                enableSearch={model.enableSearch}
                panelPosition={model.panelPosition}
                searchbarPosition={model.searchbarPosition}
                panelStyle={getPanelStyle}
                boundaryColour={model.boundaryColour}
                mapStyle={model.mapStyle}
              >
                <ComponentsContainer
                  containerId={model.content?.id}
                  dynamicComponents={
                    model.isDynamic ? model?.content?.components : []
                  }
                />
              </Map>
              <Modal
                title="Enter the details"
                open={modalOpen}
                onOk={handleModalSave}
                onCancel={handleModalCancel}
                okText="Save"
                cancelText="Cancel"
                okButtonProps={{ style: { background: '#4fb948', borderColor: '#4fb948' } }}
              >
                <Input
                  placeholder="Enter Name"
                  value={polygonName}
                  onChange={e => setPolygonName(e.target.value)}
                  onPressEnter={handleModalSave}
                  autoFocus
                />
                {/* Owner dropdown */}
                {showOwner && (
                  <Select
                    placeholder="Select owner"
                    value={owner}
                    onChange={setOwner}
                    options={ownerOptions}
                    style={{ marginTop: 12, width: '100%' }}
                    showSearch
                    optionFilterProp="label"
                  />
                )}
                {/* VAT Registered checkbox */}
                {showOwner && (
                  <Checkbox
                    checked={isRegisteredForVat}
                    onChange={e => setIsRegisteredForVat(e.target.checked)}
                    style={{ marginTop: 12 }}
                  >
                    VAT Registered
                  </Checkbox>
                )}
                {/* IrrigationType dropdown */}
                {showIrrigationType && (
                  <Select
                    placeholder="Select irrigation type"
                    value={irrigationType}
                    onChange={value => setIrrigationType(typeof value === 'number' ? value : Number(value))}
                    options={irrigationTypeOptions}
                    style={{ marginTop: 12, width: '100%' }}
                  />
                )}
                {pendingPolygon && pendingPolygon.properties && typeof pendingPolygon.properties.area === 'number' && (
                  <Input
                    style={{ marginTop: 12 }}
                    readOnly
                    value={(pendingPolygon.properties.area / 10000).toFixed(2)}
                  />
                )}
              </Modal>
            </MapProvider>
          </div>
        )}
      </ConfigurableFormItem>
    );
  },
  initModel: (model) => ({
    ...model,
    accessToken: model.accessToken,
    content: { id: nanoid(), components: [] },
    hideLabel: true,
  }),
  settingsFormMarkup: settingsForm,
  validateSettings: (model) =>
    validateConfigurableComponentSettings(settingsForm, model),
  customContainerNames: ["content"],
};

export default MapToolBox;